import { shortcut } from '@/lib/format';
import { useAuth } from '@/auth/AuthProvider';
import type { DucklingTier } from '@/types/api';
import type { Theme, ViewMode } from '@/store/uiStore';
import type { Notebook } from '@/store/notebookStore';
import { NotebookSwitcher } from '@/components/notebook/NotebookSwitcher';

export interface TitleBarProps {
  readonly notebookName: string;
  readonly onRename: (name: string) => void;
  readonly viewMode: ViewMode;
  readonly onToggleViewMode: () => void;
  readonly theme: Theme;
  readonly onToggleTheme: () => void;
  readonly objectExplorerOpen: boolean;
  readonly onToggleObjectExplorer: () => void;
  readonly columnExplorerOpen: boolean;
  readonly onToggleColumnExplorer: () => void;
  readonly tier: DucklingTier;
  readonly onTierChange: (tier: DucklingTier) => void;
  readonly onOpenPalette: () => void;
  readonly saveState: 'saved' | 'saving' | 'offline';
  readonly notebooks: readonly Notebook[];
  readonly activeNotebookId: string;
  readonly onSelectNotebook: (id: string) => void;
  readonly onCreateNotebook: () => void;
  readonly onRenameNotebook: (id: string, name: string) => void;
  readonly onDeleteNotebook: (id: string) => void;
}

export function TitleBar({
  notebookName,
  onRename,
  viewMode,
  onToggleViewMode,
  theme,
  onToggleTheme,
  objectExplorerOpen,
  onToggleObjectExplorer,
  columnExplorerOpen,
  onToggleColumnExplorer,
  tier,
  onTierChange,
  onOpenPalette,
  saveState,
  notebooks,
  activeNotebookId,
  onSelectNotebook,
  onCreateNotebook,
  onRenameNotebook,
  onDeleteNotebook,
}: TitleBarProps): JSX.Element {
  const { principal, signOut } = useAuth();

  return (
    <header className="flex h-9 shrink-0 items-center gap-2 border-b border-line bg-surface px-2">
      <button
        type="button"
        className="icon-btn"
        aria-pressed={objectExplorerOpen}
        onClick={onToggleObjectExplorer}
        title={shortcut('Toggle Object Explorer — ⌘B')}
        aria-label="Toggle Object Explorer"
      >
        ▤
      </button>

      <span className="flex items-baseline gap-1.5 pl-1">
        <span className="text-[13px] font-semibold tracking-tight text-ink">DuckDB</span>
        <span className="eyebrow">Workbench</span>
      </span>

      <span className="mx-1 h-4 w-px bg-line" aria-hidden="true" />

      <NotebookSwitcher
        notebooks={notebooks}
        activeId={activeNotebookId}
        onSelect={onSelectNotebook}
        onCreate={onCreateNotebook}
        onRename={onRenameNotebook}
        onDelete={onDeleteNotebook}
      />

      <input
        className="min-w-0 max-w-[180px] flex-shrink border-0 bg-transparent px-1 text-xs text-ink outline-none focus:bg-raised"
        value={notebookName}
        aria-label="Notebook name"
        onChange={(event) => onRename(event.target.value)}
      />

      <span
        className="eyebrow shrink-0"
        style={{ color: saveState === 'offline' ? 'var(--c-remote)' : 'var(--c-faint)' }}
        role="status"
      >
        {saveState === 'saving' ? 'Saving' : saveState === 'offline' ? 'Local only' : 'Saved'}
      </span>

      <div className="ml-auto flex items-center gap-1.5">
        <button
          type="button"
          className="btn"
          onClick={onOpenPalette}
          title={shortcut('Command palette — ⌘K')}
        >
          <span className="text-muted">Commands</span>
          <kbd className="kbd">{shortcut('⌘K')}</kbd>
        </button>

        <label className="flex items-center gap-1">
          <span className="eyebrow">Tier</span>
          <select
            className="field h-[22px] w-auto"
            value={tier}
            aria-label="Duckling tier"
            onChange={(event) => onTierChange(event.target.value as DucklingTier)}
          >
            <option value="small">small · 2 GB</option>
            <option value="medium">medium · 8 GB</option>
            <option value="large">large · 32 GB</option>
          </select>
        </label>

        <button
          type="button"
          className="icon-btn"
          aria-pressed={viewMode === 'worksheet'}
          onClick={onToggleViewMode}
          title={shortcut('Notebook / worksheet — ⌘E')}
        >
          {viewMode === 'worksheet' ? '▭' : '▤▤'}
        </button>

        <button
          type="button"
          className="icon-btn"
          onClick={onToggleTheme}
          title={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
          aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} theme`}
        >
          {theme === 'dark' ? '☀' : '☾'}
        </button>

        <button
          type="button"
          className="icon-btn"
          aria-pressed={columnExplorerOpen}
          onClick={onToggleColumnExplorer}
          title={shortcut('Toggle Column Explorer — ⌘I')}
          aria-label="Toggle Column Explorer"
        >
          ▥
        </button>

        <span className="mx-0.5 h-4 w-px bg-line" aria-hidden="true" />

        <button
          type="button"
          className="icon-btn max-w-[140px] truncate text-[11px]"
          onClick={signOut}
          title={principal ? `Signed in as ${principal.email ?? principal.sub} — sign out` : 'Sign out'}
        >
          {principal?.name ?? 'Not signed in'}
        </button>
      </div>
    </header>
  );
}
